import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import { Match, MatchStatus } from '../types/sports';
import MatchForm from '../components/matches/MatchForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Plus, Pencil, Trash2, LayoutGrid, List, CalendarDays, CalendarPlus } from 'lucide-react';
import { exportToCalendar, matchToCalendarEvent } from '../utils/calendarExport';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Matches overview page with list and card view
 */
const Matches: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState<Match | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const data = await sportsApi.getMatches();
      setMatches(data);
    } catch (err) {
      setError('Fehler beim Laden der Spiele');
      console.error('Error loading matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMatch = () => {
    setEditingMatch(undefined);
    setShowForm(true);
  };

  const handleEditMatch = (match: Match) => {
    setEditingMatch(match);
    setShowForm(true);
  };

  const handleSaveMatch = async (matchData: Omit<Match, 'id' | 'createdAt' | 'matchday' | 'homeClub' | 'awayClub'>) => {
    try {
      setFormLoading(true);

      if (editingMatch) {
        await sportsApi.updateMatch(editingMatch.id, matchData);
      } else {
        await sportsApi.createMatch(matchData);
      }

      await loadMatches();
      setShowForm(false);
      setEditingMatch(undefined);
    } catch (err) {
      console.error('Error saving match:', err);
      setError('Fehler beim Speichern des Spiels');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteMatch = async (id: number) => {
    try {
      await sportsApi.deleteMatch(id);
      await loadMatches();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting match:', err);
      setError('Fehler beim Löschen des Spiels');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingMatch(undefined);
  };

  const filteredMatches = (matches || []).filter(match => {
    if (!match) return false;

    const searchLower = searchTerm.toLowerCase();
    return (
      (match.homeClub?.name && match.homeClub.name.toLowerCase().includes(searchLower)) ||
      (match.awayClub?.name && match.awayClub.name.toLowerCase().includes(searchLower)) ||
      (match.matchday?.season?.league?.name && match.matchday.season.league.name.toLowerCase().includes(searchLower)) ||
      (match.matchday?.name && match.matchday.name.toLowerCase().includes(searchLower))
    );
  });

  const getStatusColor = (status: MatchStatus | number | null | undefined) => {
    if (status === null || status === undefined) return '#6c757d';

    const statusValue = typeof status === 'number' ? status : Number(status);

    switch (statusValue) {
      case MatchStatus.Scheduled: return '#6c757d';
      case MatchStatus.Live: return '#28a745';
      case MatchStatus.Finished: return '#007bff';
      case MatchStatus.Postponed: return '#ffc107';
      case MatchStatus.Cancelled: return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: MatchStatus | number | null | undefined) => {
    if (status === null || status === undefined) return 'Unbekannt';

    const statusValue = typeof status === 'number' ? status : Number(status);

    switch (statusValue) {
      case MatchStatus.Scheduled: return 'Geplant';
      case MatchStatus.Live: return 'Live';
      case MatchStatus.Finished: return 'Beendet';
      case MatchStatus.Postponed: return 'Verschoben';
      case MatchStatus.Cancelled: return 'Abgesagt';
      default: return 'Unbekannt';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Spiele werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler</h2>
          <p>{error}</p>
          <button onClick={loadMatches}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateMatch}>
            <Plus size={14} /> Neu
          </button>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Spiele durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredMatches.length} / {matches.length} Spiele</span>
        </div>
      </div>

      {/* Content */}
      {filteredMatches.length === 0 ? (
        <div className="page-empty-state">
          <h3>Keine Spiele gefunden</h3>
          <p>Versuchen Sie einen anderen Suchbegriff.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredMatches.map((match) => (
            <div key={match.id} className="page-card">
              <div className="page-card-header">
                <div className="page-card-matchup">
                  <div className="page-card-team">
                    <strong>{match.homeClub?.name || `Club ID: ${match.homeClubId}`}</strong>
                    {match.homeScore !== null && match.homeScore !== undefined && (
                      <span className="page-card-score">{match.homeScore}</span>
                    )}
                  </div>
                  <div className="page-card-vs">vs</div>
                  <div className="page-card-team">
                    <strong>{match.awayClub?.name || `Club ID: ${match.awayClubId}`}</strong>
                    {match.awayScore !== null && match.awayScore !== undefined && (
                      <span className="page-card-score">{match.awayScore}</span>
                    )}
                  </div>
                </div>
                <span className="page-badge" style={{ backgroundColor: getStatusColor(match.status), color: 'white' }}>
                  {getStatusText(match.status)}
                </span>
              </div>
              <div className="page-card-body">
                <div className="page-card-row">
                  <strong>Spieltag:</strong>
                  {match.matchday?.name || `Spieltag ID: ${match.matchdayId}`}
                </div>
                <div className="page-card-row">
                  <strong>Saison:</strong>
                  {match.matchday?.season?.name || 'Unbekannt'}
                </div>
                <div className="page-card-row">
                  <strong>Liga:</strong>
                  {match.matchday?.season?.league?.name || 'Unbekannt'}
                </div>
                <div className="page-card-row">
                  <strong>Sportart:</strong>
                  {match.matchday?.season?.league?.sport?.icon} {match.matchday?.season?.league?.sport?.name || 'Unbekannt'}
                </div>
                <div className="page-card-row">
                  <strong>Land:</strong>
                  {match.matchday?.season?.league?.country?.flag} {match.matchday?.season?.league?.country?.name || 'Unbekannt'}
                </div>
                <div className="page-card-row">
                  <strong>Geplant:</strong>
                  {new Date(match.scheduledDate).toLocaleString('de-DE')}
                </div>
                {match.actualDate && (
                  <div className="page-card-row">
                    <strong>Tatsächlich:</strong>
                    {new Date(match.actualDate).toLocaleString('de-DE')}
                  </div>
                )}
                {match.notes && (
                  <div className="page-card-row">
                    <strong>Notizen:</strong>
                    <p className="page-card-notes">{match.notes}</p>
                  </div>
                )}
              </div>
              <div className="page-card-footer">
                <div className="page-card-actions">
                  <button className="page-btn-icon edit" onClick={() => handleEditMatch(match)} title="Bearbeiten">
                    <Pencil size={14} />
                  </button>
                  <button className="page-btn-icon delete" onClick={() => setDeleteConfirm(match.id)} title="Löschen">
                    <Trash2 size={14} />
                  </button>
                  <button className="page-btn-icon" style={{ color: '#fd7e14' }} onClick={() => exportToCalendar(matchToCalendarEvent(match))} title="Zum Kalender hinzufügen">
                    <CalendarPlus size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Heimteam</th>
                <th>Auswärtsteam</th>
                <th>Ergebnis</th>
                <th>Spieltag</th>
                <th>Liga</th>
                <th>Sportart</th>
                <th>Datum</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map((match) => (
                <tr key={match.id}>
                  <td>{match.homeClub?.name || `Club ID: ${match.homeClubId}`}</td>
                  <td>{match.awayClub?.name || `Club ID: ${match.awayClubId}`}</td>
                  <td>
                    {(match.homeScore !== null && match.homeScore !== undefined &&
                      match.awayScore !== null && match.awayScore !== undefined)
                      ? `${match.homeScore} : ${match.awayScore}`
                      : '-'}
                  </td>
                  <td>{match.matchday?.name || `Spieltag ID: ${match.matchdayId}`}</td>
                  <td>{match.matchday?.season?.league?.name || 'Unbekannt'}</td>
                  <td>{match.matchday?.season?.league?.sport?.icon} {match.matchday?.season?.league?.sport?.name || 'Unbekannt'}</td>
                  <td>{new Date(match.scheduledDate).toLocaleString('de-DE')}</td>
                  <td>
                    <span className="page-badge" style={{ backgroundColor: getStatusColor(match.status), color: 'white' }}>
                      {getStatusText(match.status)}
                    </span>
                  </td>
                  <td>
                    <div className="page-table-actions">
                      <button className="page-btn-icon edit" onClick={() => handleEditMatch(match)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon delete" onClick={() => setDeleteConfirm(match.id)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                      <button className="page-btn-icon" style={{ color: '#fd7e14' }} onClick={() => exportToCalendar(matchToCalendarEvent(match))} title="Zum Kalender hinzufügen">
                        <CalendarPlus size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Match Form Modal */}
      {showForm && (
        <MatchForm
          match={editingMatch}
          onSave={handleSaveMatch}
          onCancel={handleCancelForm}
          isLoading={formLoading}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title="Spiel löschen"
        message="Sind Sie sicher, dass Sie dieses Spiel löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
        confirmText="Löschen"
        onConfirm={() => deleteConfirm && handleDeleteMatch(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
        type="danger"
      />
    </div>
  );
};

export default Matches;
